import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Layers, ArrowUpRight, Plus, RotateCcw, Eye } from "lucide-react";
import { OperationalShell } from "../components/OperationalShell";
import { HierarchyPanel } from "../components/HierarchyPanel";
import { ApplyTemplateDialog } from "../components/ApplyTemplateDialog";
import { CreateTemplateDialog } from "../components/CreateTemplateDialog";
import { AssignmentSummaryPanel } from "../components/AssignmentSummaryPanel";
import { GuidedArrivalBanner } from "../components/GuidedArrivalBanner";
import { useGuidedActionParams } from "../guidedAction";
import { GuidanceTooltip } from "../guidance/GuidanceTooltip";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Navigate } from "react-router-dom";

/**
 * Templates — Phase 5 “Operational Playbooks”.
 *
 * Surfaces the Template Registry and the Apply / Rollback flow. Stays
 * deliberately calm: a single column of templates on the left/right of
 * the hierarchy panel; tapping one opens a conversational impact
 * preview that an operator can read in 3 seconds. Apply is irreversible
 * UX-wise, but always carries a one-tap rollback handle.
 */
const TemplatesPage = () => {
    const { t } = useTranslation();
    const { user, loading: authLoading } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [applyTarget, setApplyTarget] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [recentAssignments, setRecentAssignments] = useState([]);
    const [summaryAssignmentId, setSummaryAssignmentId] = useState(null);

    // v4.2.1 — Guided Action arrival.
    // If we arrive with ?target=<org_id>&action=apply&guided_from=analytics,
    // resolve the org and pre-select it. We do NOT auto-open the Apply
    // dialog — the operator must still pick a template (and confirm).
    const guidedArrival = useGuidedActionParams();
    const [guidedBanner, setGuidedBanner] = useState(null);
    const [guidedHandled, setGuidedHandled] = useState(false);
    useEffect(() => {
        if (!guidedArrival.isGuided || guidedHandled) return;
        const { target, target_name, source, source_name } = guidedArrival.params;
        if (target) {
            // Pre-select even before the hierarchy fetches — Templates
            // page only needs `selectedOrg.id` to enable apply buttons.
            setSelectedOrg({
                id: target,
                name: target_name || "",
            });
        }
        setGuidedBanner({
            source_name: source_name || "",
            target_name: target_name || "",
            source,
            target,
        });
        setGuidedHandled(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guidedArrival.isGuided]);

    const fetchTemplates = async () => {
        try {
            const res = await api.get("/operational-base/templates");
            setTemplates(res.data?.templates || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load templates");
        } finally {
            setLoading(false);
        }
    };

    const fetchAssignments = async () => {
        try {
            const params = selectedOrg ? `?target_org_id=${selectedOrg.id}` : "";
            const res = await api.get(`/operational-base/template-assignments${params}`);
            setRecentAssignments(res.data?.assignments || []);
        } catch (_) {
            // non-fatal
        }
    };

    useEffect(() => { fetchTemplates(); }, []);
    useEffect(() => { fetchAssignments(); }, [selectedOrg]);

    const onRollback = async (assignmentId) => {
        if (!window.confirm(t("ob.templates.rollback_confirm"))) return;
        try {
            await api.post(`/operational-base/template-assignments/${assignmentId}/rollback`, {
                reason: "Rolled back by operator",
            });
            toast.success(t("ob.templates.rollback_success"));
            fetchAssignments();
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("ob.templates.rollback_error"));
        }
    };

    if (authLoading) return null;
    if (!user) return <Navigate to="/login" replace />;

    return (
        <OperationalShell breadcrumb={[{ label: t("ob.templates.eyebrow") }]}>
            <header className="mb-10 lg:mb-14 space-y-3" data-testid="ob-templates-header">
                <p className="ob-eyebrow">{t("ob.templates.eyebrow")}</p>
                <h1 className="text-4xl sm:text-5xl flex items-center" style={{ color: "var(--ob-text-primary)" }} data-testid="ob-templates-title">
                    {t("ob.templates.title")}
                    <GuidanceTooltip guidanceKey="templates.intro" />
                </h1>
                <p className="text-base max-w-2xl" style={{ color: "var(--ob-text-secondary)" }}>
                    {t("ob.templates.subtitle")}
                </p>
            </header>

            {guidedBanner && (
                <GuidedArrivalBanner
                    title={
                        guidedBanner.target_name
                            ? t("ob.templates.guided_apply_title", {
                                  target: guidedBanner.target_name,
                              })
                            : t("ob.guided.from_analytics")
                    }
                    explanation={
                        guidedBanner.source_name && guidedBanner.target_name
                            ? t("ob.templates.guided_apply_explanation", {
                                  source: guidedBanner.source_name,
                                  target: guidedBanner.target_name,
                              })
                            : t("ob.templates.guided_default_explanation")
                    }
                    evidence={null}
                    onDismiss={() => setGuidedBanner(null)}
                    testId="ob-templates-guided-banner"
                />
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-8">
                <aside className="lg:order-1">
                    <HierarchyPanel selectedOrgId={selectedOrg?.id} onSelectOrg={setSelectedOrg} />
                </aside>
                <div className="space-y-8 lg:order-2">
                    <section>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="ob-eyebrow">{t("ob.templates.registry_section")}</h2>
                            <button
                                type="button"
                                onClick={() => setCreateOpen(true)}
                                data-testid="ob-templates-create-btn"
                                className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5"
                                style={{
                                    color: "#A7F3D0",
                                    background: "rgba(16,185,129,0.10)",
                                    border: "1px solid rgba(16,185,129,0.25)",
                                }}
                            >
                                <Plus size={12} strokeWidth={1.5} aria-hidden="true" />
                                {t("ob.templates.create_btn")}
                            </button>
                        </div>
                        {loading ? (
                            <p className="text-sm" style={{ color: "var(--ob-text-tertiary)" }}>
                                {t("ob.activity.loading")}
                            </p>
                        ) : templates.length === 0 ? (
                            <div className="ob-card p-6" data-testid="ob-templates-empty">
                                <p className="text-sm" style={{ color: "var(--ob-text-secondary)" }}>
                                    {t("ob.templates.empty_state")}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3" data-testid="ob-templates-list">
                                {templates.map((tmpl) => (
                                    <TemplateCard
                                        key={tmpl.id}
                                        template={tmpl}
                                        canApply={!!selectedOrg}
                                        targetOrgName={selectedOrg?.name}
                                        onApply={() => setApplyTarget({ template: tmpl, org: selectedOrg })}
                                    />
                                ))}
                            </div>
                        )}
                    </section>

                    {recentAssignments.length > 0 && (
                        <section>
                            <h2 className="ob-eyebrow mb-4">{t("ob.templates.recent_section")}</h2>
                            <div className="space-y-2">
                                {recentAssignments.slice(0, 6).map((a) => (
                                    <AssignmentRow
                                        key={a.id}
                                        assignment={a}
                                        onRollback={onRollback}
                                        onWhatChanged={() => setSummaryAssignmentId(a.id)}
                                    />
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            </div>

            {applyTarget && (
                <ApplyTemplateDialog
                    template={applyTarget.template}
                    targetOrg={applyTarget.org}
                    onClose={() => setApplyTarget(null)}
                    onSuccess={() => {
                        setApplyTarget(null);
                        fetchAssignments();
                    }}
                />
            )}
            {createOpen && (
                <CreateTemplateDialog
                    onClose={() => setCreateOpen(false)}
                    onSuccess={() => {
                        setCreateOpen(false);
                        fetchTemplates();
                    }}
                />
            )}
            {summaryAssignmentId && (
                <AssignmentSummaryPanel
                    assignmentId={summaryAssignmentId}
                    onClose={() => setSummaryAssignmentId(null)}
                    onRolledBack={fetchAssignments}
                />
            )}
        </OperationalShell>
    );
};

const TemplateCard = ({ template, canApply, targetOrgName, onApply }) => {
    const { t } = useTranslation();
    return (
        <div
            data-testid={`ob-template-card-${template.id}`}
            className="ob-card p-5 sm:p-6 flex items-start justify-between gap-4"
        >
            <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className="ob-tile-icon flex-shrink-0" aria-hidden="true">
                    <Layers size={20} strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                    <p className="text-base font-medium" style={{ color: "var(--ob-text-primary)", fontFamily: "Outfit, sans-serif" }} data-testid="ob-template-card-name">
                        {template.name}
                    </p>
                    <p className="text-xs mt-1" style={{ color: "var(--ob-text-tertiary)" }}>
                        {t("ob.templates.version_label", { v: template.current_version })}
                        {template.description ? ` · ${template.description}` : ""}
                    </p>
                </div>
            </div>
            <button
                type="button"
                disabled={!canApply}
                onClick={onApply}
                data-testid={`ob-template-apply-${template.id}`}
                className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 disabled:opacity-40"
                style={{
                    color: "#A7F3D0",
                    background: "rgba(16,185,129,0.10)",
                    border: "1px solid rgba(16,185,129,0.25)",
                    cursor: canApply ? "pointer" : "not-allowed",
                }}
                title={canApply
                    ? t("ob.templates.apply_to", { name: targetOrgName })
                    : t("ob.templates.apply_select_first")}
            >
                <ArrowUpRight size={12} strokeWidth={1.5} aria-hidden="true" />
                {canApply ? t("ob.templates.apply_to", { name: targetOrgName }) : t("ob.templates.apply_select_first")}
            </button>
        </div>
    );
};

const AssignmentRow = ({ assignment, onRollback, onWhatChanged }) => {
    const { t } = useTranslation();
    return (
        <div
            data-testid={`ob-assignment-row-${assignment.id}`}
            className="rounded-xl px-4 py-3 flex items-center justify-between gap-3"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
        >
            <div className="min-w-0 flex-1">
                <p className="text-sm" style={{ color: "var(--ob-text-primary)" }}>
                    {assignment.template_name}
                    <span style={{ color: "var(--ob-text-tertiary)" }}>
                        {" · "}{t("ob.templates.version_label", { v: assignment.version_applied })}
                        {" · "}{assignment.target_org_name}
                    </span>
                </p>
                <p className="text-xs mt-0.5" style={{ color: "var(--ob-text-tertiary)" }}>
                    {assignment.active
                        ? t("ob.templates.assignment_active")
                        : t("ob.templates.assignment_rolled_back")}
                </p>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                    type="button"
                    onClick={() => onWhatChanged && onWhatChanged()}
                    data-testid={`ob-assignment-what-changed-${assignment.id}`}
                    className="inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1"
                    style={{
                        color: "#A7F3D0",
                        background: "rgba(16,185,129,0.08)",
                        border: "1px solid rgba(16,185,129,0.20)",
                    }}
                    title={t("ob.assignment_summary.entry_tooltip")}
                >
                    <Eye size={11} strokeWidth={1.5} aria-hidden="true" />
                    {t("ob.assignment_summary.entry_button")}
                </button>
                {assignment.active && (
                    <button
                        type="button"
                        onClick={() => onRollback(assignment.id)}
                        data-testid={`ob-assignment-rollback-${assignment.id}`}
                        className="inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1"
                        style={{ color: "#93C5FD", background: "rgba(59,130,246,0.07)", border: "1px solid rgba(59,130,246,0.20)" }}
                    >
                        <RotateCcw size={11} strokeWidth={1.5} aria-hidden="true" />
                        {t("ob.templates.rollback_btn")}
                    </button>
                )}
            </div>
        </div>
    );
};

export default TemplatesPage;
